import React from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { User } from '../types';
import { Box, Card, CardContent, Typography, Alert } from '@mui/material';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const ALLOWED_EMAIL = 'andrewngo@sleekshop.com';

interface GoogleAuthProps {
  onSuccess: (user: User) => void;
  onError?: (error: string) => void;
}

export function GoogleAuth({ onSuccess, onError }: GoogleAuthProps) {
  const handleLogin = (credentialResponse: any) => {
    try {
      const idToken: string | undefined = credentialResponse?.credential;
      if (!idToken) {
        throw new Error('Missing Google credential');
      }
      const decoded: any = jwtDecode(idToken);
      
      // CRITICAL: Only allow specific email
      if (decoded.email === ALLOWED_EMAIL) {
        const user: User = {
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture,
          sub: decoded.sub
        };
        // Persist user and token
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('google_token', idToken);
        onSuccess(user);
      } else {
        const errorMsg = 'Access denied. This dashboard is restricted to Andrew Ngo.';
        if (onError) {
          onError(errorMsg);
        } else {
          alert(errorMsg);
        }
      }
    } catch (error) {
      const errorMsg = 'Authentication failed. Please try again.';
      if (onError) {
        onError(errorMsg);
      } else {
        alert(errorMsg);
      }
    }
  };

  const handleError = () => {
    const errorMsg = 'Login failed. Please try again.';
    if (onError) {
      onError(errorMsg);
    } else {
      console.log('Login Failed');
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            SleekShop CEO Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Access restricted to authorized personnel only
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Only andrewngo@sleekshop.com can access this dashboard
          </Alert>

          {!GOOGLE_CLIENT_ID ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Configuration Error:</strong> Google OAuth Client ID not configured.
                <br />
                Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file.
              </Typography>
            </Alert>
          ) : (
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={handleLogin}
                onError={handleError}
                hosted_domain="sleekshop.com"
                theme="filled_blue"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </GoogleOAuthProvider>
          )}
        </CardContent>
      </Card>
    </Box>
  );
} 